// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { BrandDto } from "@insite/client-framework/Types/ApiModels";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC, PropsWithChildren } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { brandProps } from "@store/Themes/common/props/brand";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { bubbleStyles } from "@store/Themes/common/mixins/bubble";
/* Store Custom Code - End */

interface OwnProps {
    brand: BrandDto;
    /**
     * If true, the small brand logo will be displayed.
     * If false, the brand name will be displayed instead.
     * Default value: false */
    showLogo?: boolean;
    extendedStyles?: ProductBrandStyles;
    // Starter Custom Code
    overridedStyles?: ProductBrandStyles;
}

type Props = OwnProps;

export interface ProductBrandStyles {
    logoImage?: LazyImageProps;
    nameLink?: LinkPresentationProps;
    nameText?: TypographyProps;
    // Starter Custom Code
    wrapper?: InjectableCss;
    // Store Custom Code
    bubble?: InjectableCss;
}

// Starter Removed - nameText

export const productBrandStyles: ProductBrandStyles = {
    wrapper: {
        css: css`
            color: ${getColor("secondary")}; /* Store Custom Code */
            font-size: ${x => x.theme.design.typography.size.smaller}; /* Store Custom Code */
            font-weight: ${x => x.theme.design.typography.weight.semibold}; /* Store Custom Code */
            line-height: ${x => x.theme.design.typography.lineHeight.larger}; /* Store Custom Code */
            text-transform: uppercase;
            /* Store Custom Code - Start */
            display: flex;
            gap: ${pxToRem(5)};
            align-items: center;
            /* Store Custom Code - End */
        `,
    },
    logoImage: brandProps.logoSmall,
    nameLink: {
        css: linkStyles("inherit", "text.linkAltHover", false, true),
        typographyProps: {
            css: css`
                display: flex;
            `,
        },
    },
    // Store Custom Code
    bubble: {
        css: css`
            ${bubbleStyles()}
        `,
    },
};

const ProductBrand: FC<PropsWithChildren<Props>> /* Starter Custom Code */ = ({
    brand,
    showLogo = false,
    extendedStyles,
    // Starter Custom Code
    children,
    overridedStyles,
    ...htmlAttributes
}) => {
    if (!brand) {
        return null;
    }

    const styles = useExpandProps(productBrandStyles, extendedStyles, overridedStyles); // Starter Custom Code

    let brandDisplay;
    if (showLogo && brand.logoSmallImagePath) {
        const altText = brand.logoImageAltText || brand.name;
        brandDisplay = <LazyImage {...styles.logoImage} src={brand.logoSmallImagePath} altText={altText} />;
    } else {
        brandDisplay = brand.detailPagePath ? (
            brand.name
        ) : (
            <Typography {...styles.nameText} data-test-selector="brandName">
                {brand.name}
            </Typography>
        );
    }

    const output = brand.detailPagePath ? ( // Starter Custom Code
        <Link {...styles.nameLink} href={brand.detailPagePath} data-test-selector="brandLink">
            {brandDisplay}
        </Link>
    ) : (
        <>{brandDisplay}</>
    );

    /* Starter Custom Code - Start */
    return (
        <StyledWrapper {...htmlAttributes} {...styles.wrapper}>
            <StyledWrapper {...styles.bubble}></StyledWrapper>
            {output}
        </StyledWrapper>
    );
    /* Starter Custom Code - End */
};

export default ProductBrand;
